import React, { useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useTypedSelector } from '../../state/hooks';
import Spacer from '../layout/Spacer';
import MessagePrompter from '../prompts/MessagePrompter';
import Dialog from '../layout/Dialog';
import { ExportType } from '../../globals';
import DropdownProperty from '../properties/DropdownProperty';
import { refreshExportsFromServer } from '../filebrowser/FileManager';
import APIUtility from '../../classes/APIUtility';
import { Save } from '../../interfaces/Responses';
import StandbyOverlay from '../layout/StandbyOverlay';

type ExportProjectDialogProps = {
	onShutdown?: () => void;
	revision?: Save;
}

function GetSelectedSaveText({date, version, isMostRecent}: {date: string, version: string; isMostRecent: boolean })
{  
	const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	const jsDate = new Date(date);

	return <div className={"instructionsTextGrey"}>
		{(isMostRecent ? "Most recent save:" : "Save:") + " " + monthNames[jsDate.getMonth()]+ " " + jsDate.getDate() + ", " +jsDate.getFullYear() + ", " + jsDate.getHours() + ":" + jsDate.getMinutes() + ". Create " +version}
		<p></p>
	</div>
}

function GetInstructionText({exportType, save, isMostRecent}: {exportType: ExportType; save?: Save, isMostRecent: boolean})
{
	let instructions: JSX.Element;
	switch(exportType)
	{
		case ExportType.NoAssets:
			instructions = <p>Export the most recent save of your project, without referenced assets.<br/>Will result in missing assets if used outside of your organization.</p>;
			break;
		case ExportType.AssetsEmbedded:
			instructions  = <p>Export the most recent save of your project, including its referenced (processed) assets.</p>;
			break;
		case ExportType.Full:
			instructions  = <p>Export the most recent save of your project, including its referenced (processed) assets.<br/>The original, unprocessed CAD data will be packaged as well.</p>;
			break;
	}
	return <div className={"instructionsText"}>
		{instructions}
		{
			save && <GetSelectedSaveText 
				date={save.creationDate || ""}
				version={save.createVersion}
				isMostRecent={isMostRecent}/>
		}
	</div>
}
	
function ExportProjectDialog(props: ExportProjectDialogProps)
{
	const latestSaveData = useTypedSelector(state => state.applicationModule.latestSaveData, shallowEqual);
	const [selectedExportType, setSelectedExportType] = useState<ExportType>(ExportType.AssetsEmbedded);

	function onSD()
	{
		if(props.onShutdown)
		{
			props.onShutdown();
		}
	}

	return (
		<Dialog
			title={"Export Project"}
			className={"exportDialogRoot"}
			data-name={"export-dialog-root"}
			onShutdown={onSD}
			actions={[
				{
					label: "Confirm",
					onClick: ()=>
					{
						if(props.revision) 
						{	
							StandbyOverlay.startStandbyMode("Exporting Project");
							APIUtility.exportProjectDownload(selectedExportType, props.revision).then((data)=>
							{
								const blob = new Blob([data]);
								const url = window.URL.createObjectURL(blob);
								const a = document.createElement("a");
								a.href = url;
								a.download = `export_${props.revision?.project}_${props.revision?.revisionId}.zip`;
								a.click();
								StandbyOverlay.stopStandbyMode();
							}).catch(error=>
							{
								MessagePrompter.showError({
									code: error.response?.data.code || error.code,
									title: error.response?.data.message || error.message,
									content: <div>
										{error.response?.data.info || error.name}
									</div>,
								});
								StandbyOverlay.stopStandbyMode();
							})
						}
						else 
						{
							APIUtility.exportProject(selectedExportType).then((data)=>
							{
								console.log(data);
								refreshExportsFromServer();
							}).catch(
								(error)=>
								{
									switch(error.response.data.code)
									{
										case 1101: //Asset is not an archive
										case 2103: //Access to this project is restricted
										case 2109: //Import in progress
										case 2110: //Export in progress
										case 2111: //Unspecified error
										case 2112: //Export revision failed
										case 2113: //Export revision timed out
										case 2114: //Export revision streaming failed
										case 2115: //Export revision timed out
											MessagePrompter.showError({
												code: error.response.data.code,
												title: error.response.data.message,
												content: <div>
													{error.response.data.info}
												</div>,
											});
											break;
										default:
											MessagePrompter.showError({
												code: error.response.status,
												title: error.response.statusText,
												content: <div>
													{error.response.data.error}
												</div>,
											});
											break;
									}
								}
							)
						} 
					}
				},
				{
					label: "Cancel",
				}
			]}
		>
			<DropdownProperty
				label={"Export Type"}
				options={[
					{ display: 'Project, assets and CAD', value: ExportType.Full }, 
					{ display: 'Project and assets', value: ExportType.AssetsEmbedded }, 
					{ display: 'Project without assets', value: ExportType.NoAssets },
				]}
				baseStyle={{ height:'24px' }}
				value={selectedExportType}
				onChangeCallback={(newValue: ExportType) =>
				{
					setSelectedExportType(newValue);
				}}
			/>
			<Spacer size={{ y:"2px" }}/>
			<GetInstructionText
				exportType={selectedExportType}
				save={props.revision || latestSaveData}
				isMostRecent={!props.revision}
			/>
		</Dialog>
	);
}

export default ExportProjectDialog;